import { useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { observer } from 'mobx-react-lite';

import { useLocalStorageState } from '@components/component-helpers';
import {
  ActionFn,
  ColumnSettings,
  ColumnSettingsChange,
  ColumnSort,
  Columns,
  DataGrid,
} from '@components/data-grid';
import { sortByObjectKey } from '@utils/collections';
import { compareStrings } from '@utils/strings';

import { Quickfilter } from 'components/Quickfilter';
import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';
import { useStore } from 'models/Provider';
import { IEmailTemplate } from 'models/types';

export const EmailTemplates = observer(() => {
  const { templateStore, t } = useStore();

  const [sort, setSort] = useLocalStorageState<ColumnSort<IEmailTemplate>>({
    key: 'email-template-sort',
    defaultValue: {
      key: 'title',
      order: 'ascending',
    },
  });

  const [settings, setSettings] = useLocalStorageState<ColumnSettings<IEmailTemplate>>({
    key: 'email-templates-columns',
    defaultValue: {
      title: { order: 0, visible: true },
      templateType: { order: 1, visible: true },
      isDefault: { order: 2, visible: true },
    },
  });

  const [tabIndex, setTabIndex] = useLocalStorageState({
    key: 'email-templates-tab',
    defaultValue: 0,
    valueAsNumber: true,
  });

  let props = {
    settings,
    sort,
    onSortChange: setSort,
    onSettingsChange: setSettings,
  };

  return (
    <Quickfilter
      onChange={setTabIndex}
      defaultIndex={tabIndex}
      items={[
        {
          count: templateStore.email.all.length,
          label: t('company.templates.email.tabs.all'),
          render: () => <EmailTemplateList templates={templateStore.email.all} {...props} />,
        },
        {
          count: templateStore.email.message.length,
          label: t('company.templates.email.tabs.message'),
          render: () => <EmailTemplateList templates={templateStore.email.message} {...props} />,
        },
        {
          count: templateStore.email.newApplication.length,
          label: t('company.templates.email.tabs.new_application'),
          render: () => (
            <EmailTemplateList templates={templateStore.email.newApplication} {...props} />
          ),
        },
        {
          count: templateStore.email.rejection.length,
          label: t('company.templates.email.tabs.rejection'),
          render: () => <EmailTemplateList templates={templateStore.email.rejection} {...props} />,
        },
      ]}
    />
  );
});

type TemplateListProps<T> = {
  templates: Array<T>;
  settings: ColumnSettings<T>;
  sort: ColumnSort<T>;
  onSortChange(newSort: ColumnSort<T>): void;
  onSettingsChange: ColumnSettingsChange<T>;
};

function EmailTemplateList({
  templates,
  settings,
  sort,
  onSortChange,
  onSettingsChange,
}: TemplateListProps<IEmailTemplate>) {
  const { t } = useStore();
  const { showModal } = useModal();
  const { hasRolePermission } = usePermissions();
  const navigate = useNavigate();
  const columns = useRef<Columns<IEmailTemplate>>([
    {
      label: t('company.templates.email.label.title'),
      key: 'title',
      sortable: true,
      type: 'text-long',
    },
    {
      label: t('company.templates.email.label.type'),
      key: 'templateType',
      sortable: true,
      render: (item) => t(`company.templates.type.${item.templateType}`),
    },
    {
      label: t('company.templates.email.label.default'),
      key: 'isDefault',
      render: (item) => {
        if (item.templateType !== 'applicant_new_application') return '-';
        return t(`company.templates.default.${item.isDefault ? 'yes' : 'no'}`);
      },
    },
  ]).current;

  const sortedTemplates = useMemo(() => {
    let desc = sort.order === 'descending';

    if (sort.key === 'title') {
      return sortByObjectKey(templates, 'title', desc);
    }

    if (sort.key === 'templateType') {
      return templates.sort((a, b) => {
        const aType = t(`company.templates.type.${a.templateType}`);
        const bType = t(`company.templates.type.${b.templateType}`);
        return compareStrings(aType, bType, !desc);
      });
    }

    return templates;
  }, [t, templates, sort]);

  const deleteTemplate = useCallback(
    (template: IEmailTemplate) => {
      // Since the modal isn't typed, this callsite incorrectly thinks
      // `onCancel` is a required parameter.
      // @ts-expect-error
      showModal('CUSTOM_CONFIRM', {
        danger: true,
        data: {
          text: (
            <div>
              {t('company.templates.email.delete.text')}{' '}
              <strong>{template.title || template.name}</strong>
            </div>
          ),
        },
        confirm: template.delete,
      });
    },
    [showModal, t],
  );

  const actionsForRow = useCallback<ActionFn<IEmailTemplate>>(
    (template) => {
      if (template.isDefault && template.templateType !== 'message') return [];

      return [
        {
          label: t('company.templates.email.menu.delete'),
          onSelect: () => deleteTemplate(template),
          variant: 'danger',
        },
      ];
    },
    [deleteTemplate, t],
  );

  return (
    <DataGrid<IEmailTemplate>
      label="Email templates"
      columns={columns}
      sort={sort}
      onSortChange={onSortChange}
      columnSettings={settings}
      onColumnSettingsChange={onSettingsChange}
      data={sortedTemplates}
      actions={!hasRolePermission('company:edit-templates') ? undefined : actionsForRow}
      onRowClick={{
        label: t('company.templates.email.menu.edit'),
        callback: (template) => {
          navigate(`/settings/templates/emails/${template.id}`);
        },
      }}
    />
  );
}
